import * as _ from 'lodash';
import { useCallback, useMemo } from 'react';
// eslint-disable-next-line lodash/import-scope
import type { DebounceSettings } from 'lodash';

import useOnUnmount from './useOnUnmount';

const DEFAULT_TIMEOUT = 500;

//  By default, we want the function to be invoked after the last time it is called.
//  We want requests to happen after the last user action takes place.
//  Read more: https://css-tricks.com/debouncing-throttling-explained-examples/#aa-debounce
const DEFAULT_DEBOUNCE_OPTIONS: DebounceSettings = {
  leading: false,
  trailing: true,
};

/**
 * Hook for invoking a debounced callback after a given timeout.
 *
 * @param {Function} [func] - Function to be debounced.
 * @param {number} [wait] - Debounce timeout.
 * @param {DebounceSettings} [settings] - Debounce settings.
 * @example
 * const MySearch = () => {
 *   const [value, setSearch] = useState('');
 *   const fetchSearch = useDebouncedCallback(search => get(`/api/search?q=${search}`));
 *   const handleSearch = (e) => {
 *     //  Update state right away
 *     setSearch(e.target.value);
 *     // Call the debounced fetch
 *     fetchSearch(e.target.value);
 *   };
 *   return (
 *     <input
 *       value={value}
 *       onChange={handleSearch}
 *     />
 *   );
 * }
 */
const useDebouncedCallback = <T extends (...args: unknown[]) => unknown> (
  func: T,
  wait: number = DEFAULT_TIMEOUT,
  settings: DebounceSettings = DEFAULT_DEBOUNCE_OPTIONS,
) => {
  //  Our memoized/stable ref debounce function.
  //  eslint-disable-next-line react-hooks/exhaustive-deps -- This is intentional, we don't want the debounce ref to change.
  const debouncedCallback = useMemo(() => _.debounce(func, wait, settings), []);

  /**
   * When the component unmounts, we want to ensure that we
   * clear any current debounces invocations from happening.
   */
  const cleanup = useCallback(() => {
    debouncedCallback?.cancel?.();
  }, [debouncedCallback]);

  useOnUnmount(cleanup);

  return debouncedCallback;
};

export default useDebouncedCallback;
